import { useMemo } from 'react';
import type { Control, FieldValues, Path } from 'react-hook-form';
import ControlledSelect from 'src/components/form/controlled-select';
import useEntityInfo from 'src/hooks/getEntityInfo';
import { useAggregation } from 'src/hooks/useAggregation';
import { notEmpty } from 'src/utilityTypes';
import { useFormConfigRegistry } from 'src/utils/table/hooks/form/useFormConfigRegistry';

import { useGetFormConfiguration } from '@/hooks/queries';
import { getColumnHeader } from '@/utils/table/hooks/getColumnHeader';
import {
  convertSchemasToFieldConfigs,
  getHiddenCustomAttributePaths,
} from '@/utils/table/utils/customAttributes';

import type { WidgetDataSource } from '../../gigawidget/types';
import {
  createStandardFieldLabelResolver,
  getCustomAttributeOptions,
  getDateCustomAttributeOptions,
  getDateFieldOptions,
  getStandardFieldOptions,
} from './formHelpers';

type Props<T extends FieldValues, TDataSource extends WidgetDataSource> = {
  dataSource: TDataSource;
  name: Path<T>;
  label: string;
  includeDateValues?: boolean;
  onlyDateFields?: boolean;
  control: Control<T>;
  testId: string;
  addEmptyOption?: boolean;
};

export const CategorySelector = <
  T extends FieldValues,
  TDataSource extends WidgetDataSource,
>({
  dataSource,
  label,
  name,
  includeDateValues,
  onlyDateFields,
  control,
  testId,
  addEmptyOption,
}: Props<T, TDataSource>) => {
  const categoryGetters = dataSource.categoryGetters;
  const { riskModel } = useAggregation();
  const isAggregationsEnabled = riskModel !== 'default';
  const filterOptions = { isAggregationsEnabled };

  const { data, loading } = useGetFormConfiguration({
    queryArgs: { parentTypes: dataSource.parentTypes },
  });
  const getEntityInfo = useEntityInfo();
  const formRegistry = useFormConfigRegistry();
  const resolveStandardFieldLabel = useMemo(
    () => createStandardFieldLabelResolver(data?.form_configuration),
    [data?.form_configuration]
  );
  const customAttributeOptions = useMemo(() => {
    const hiddenPaths = getHiddenCustomAttributePaths(
      data?.form_configuration.flatMap((config) => config.fields_config ?? [])
    );
    const fieldConfig = convertSchemasToFieldConfigs({
      customAttributeSchemas:
        data?.form_configuration
          .map((config) => config.customAttributeSchema)
          .filter(notEmpty) ?? [],
      enableRelativeDates: true,
      hiddenPaths,
    });

    if (fieldConfig) {
      return Object.entries(fieldConfig).map(([id, config]) => ({
        value: `custom:${id}`,
        label: getColumnHeader(
          {
            formConfigurations: data?.form_configuration ?? null,
            formRegistry,
            getEntityInfo,
          },
          config
        ),
      }));
    }
  }, [data?.form_configuration, formRegistry, getEntityInfo]);

  const dateCustomAttributeOptions = useMemo(
    () =>
      getDateCustomAttributeOptions(
        customAttributeOptions,
        data?.form_configuration
      ),
    [customAttributeOptions, data?.form_configuration]
  );

  return (
    <ControlledSelect
      addEmptyOption={addEmptyOption}
      testId={testId}
      placeholder={'Select category'}
      disabled={!categoryGetters}
      statusType={loading ? 'loading' : 'finished'}
      options={
        onlyDateFields
          ? [
              ...getDateFieldOptions(
                categoryGetters,
                true,
                filterOptions,
                resolveStandardFieldLabel
              ),
              ...getCustomAttributeOptions(dateCustomAttributeOptions),
            ]
          : [
              ...getStandardFieldOptions(
                categoryGetters,
                filterOptions,
                resolveStandardFieldLabel
              ),
              ...getDateFieldOptions(
                categoryGetters,
                includeDateValues,
                filterOptions,
                resolveStandardFieldLabel
              ),
              ...getCustomAttributeOptions(customAttributeOptions),
            ]
      }
      name={name}
      label={label}
      control={control}
    />
  );
};
